<script lang="ts">
export default {
  name: 'CompaniesSnsInstagramHashtagForm',
}
</script>
<script setup lang="ts">
import {
  computed,
  ref
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import {
  useRoute,
  useRouter
} from 'vue-router'

import {
  KkIcon,
  KkButton,
  KkLoading,
  KkBox,
  KkFormLabel,
  KkTextField,
  KkValidationError
} from '../../../../components/base'
import { KkAddLabel } from '../../../../components/ui'
import { KkFilterModal, KkSelectStore, KkStoreModal } from '../../../../components/projects'
import {
  KkAccountInfoLabel
} from '../../../../components/domains/sns'
import { addAttributes } from '../../../../modules/filterModalConst'
import type { Account } from '../../../../components/domains/sns/types'
import type { Store, TagGroup } from '../../../../types'

interface FormValue {
  brands: number[]
  franchisees: number[]
  user_companies: number[]
  areas: number[]
  prefectures: number[]
  original_tag_group_a: number[]
  original_tag_group_b: number[]
  original_tag_group_c: number[]
  original_tag_group_d: number[]
  original_tag_group_e: number[]
  [key: string]: number[]
}
interface StoreModalFormValue {
  stores: string[]
}
interface FilterItem {
  label: string
  kind: string
  tags: TagGroup[]
  type: string
  headText: string
  filterName: string
}
interface PayloadSettings {
  store_uuids: string[]
  keywords: string[]
}
interface HashtagUploadPayload {
  settings: PayloadSettings
}
type ReadStoreQuery = {
  companyUuid: string
  params: {
    per: number
    page: number
    q: {
      m: 'or'
    }
    search: {
      [key: string]: number[]
    }
  }
}

const store = useStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const currentCompanyUuid = computed<string>(() => route.params.company_uuid as string)
const instagramBusinessAccountId = computed<string>(() => route.params.instagram_business_account_id as string)

const hashtags = ref<string[]>([''])
const filterItems = ref<FilterItem[]>([])
const storeUuids = ref<string[]>([])
const showFilterModal = ref<boolean>(false)
const showStoreModal = ref<boolean>(false)

const instagramBusinessAccounts = computed<Account[]>(() => store.getters['companiesSnsInstagramBusinessAccounts/instagramBusinessAccounts'])
const instagramBusinessAccountsLoading = computed<boolean>(() => store.getters['companiesSnsInstagramBusinessAccounts/loading'])
const instagramHashtagSetting = computed<Account>(() => store.getters['companiesSnsInstagramHashtag/hashtagSetting'])
const instagramHashtagLoading = computed<boolean>(() => store.getters['companiesSnsInstagramHashtag/loading'])
const storeTotalCount = computed<number>(() => store.getters['companiesSnsInstagramHashtag/storeTotalCount'])
const searchAttributesLoading = computed<boolean>(() => store.getters['companiesSearchTagGroups/loading'])
const formValues = computed<FormValue>(() => store.getters['companiesSearchTagGroups/formValues'])
const brands = computed<TagGroup>(() => store.getters['companiesSearchTagGroups/brands'])
const areas = computed<TagGroup>(() => store.getters['companiesSearchTagGroups/areas'])
const franchisees = computed<TagGroup>(() => store.getters['companiesSearchTagGroups/franchisees'])
const userCompanies = computed<TagGroup>(() => store.getters['companiesSearchTagGroups/userCompanies'])
const originalTagGroupA = computed<TagGroup>(() => store.getters['companiesSearchTagGroups/originalTagGroupA'])
const originalTagGroupB = computed<TagGroup>(() => store.getters['companiesSearchTagGroups/originalTagGroupB'])
const originalTagGroupC = computed<TagGroup>(() => store.getters['companiesSearchTagGroups/originalTagGroupC'])
const originalTagGroupD = computed<TagGroup>(() => store.getters['companiesSearchTagGroups/originalTagGroupD'])
const originalTagGroupE = computed<TagGroup>(() => store.getters['companiesSearchTagGroups/originalTagGroupE'])
const prefectures = computed<TagGroup>(() => store.getters['companiesSearchTagGroups/prefectures'])

const readStores = (payload: ReadStoreQuery): Promise<void> => store.dispatch('companiesLocationStores/readStores', payload)
const readSearchTagGroups = (): Promise<void> => store.dispatch('companiesSearchTagGroups/readSearchTagGroups')
const setFormValues = (payload: FormValue): Promise<void> => store.dispatch('companiesSearchTagGroups/setFormValues', payload)
const setFormValuesOfSnsInstagramHashtag = (payload: StoreModalFormValue): Promise<void> => store.dispatch('companiesSnsInstagramHashtag/setFormValues', payload)
const readInstagramBusinessAccounts = (): Promise<void> => store.dispatch('companiesSnsInstagramBusinessAccounts/readInstagramBusinessAccounts')
const readHashtagSetting = (instagramBusinessAccountId: string): Promise<void> => store.dispatch('companiesSnsInstagramHashtag/readHashtagSetting', instagramBusinessAccountId)
const updateHashtagSetting = (payload: {payload: HashtagUploadPayload, instagramBusinessAccountId: string}): Promise<boolean> => store.dispatch('companiesSnsInstagramHashtag/updateHashtagSetting', payload)
const filteredCount = computed(() => store.state.companiesLocationStores.filteredCount)
const searchStoresLoading = computed(() => store.state.companiesLocationStores.loading)
const stores = computed<{ attributes: { uuid: string, connected_gmb: boolean, external_accounts: [], name: string } }[]>(() => store.state.companiesLocationStores.data)
const instagramHashtagFormValues = computed(() => store.state.companiesSnsInstagramHashtag.formValues)

const setInstagramHashtagFormValues = (payload: StoreModalFormValue) => store.commit('companiesSnsInstagramHashtag/setFormValues', payload)
const toggleInstagramHashtagFormValue = (payload: { allChecked: boolean; initialValues: string[]; target: 'stores' }) => store.commit('companiesSnsInstagramHashtag/toggleFilterValues', payload)
const setInstagramHashtagFilterValues = (payload: { target: 'stores'; value: string }) => store.commit('companiesSnsInstagramHashtag/setFilterValues', payload)

const formInitialValues = (): FormValue => {
  return {
    brands: [],
    franchisees: [],
    user_companies: [],
    areas: [],
    prefectures: [],
    original_tag_group_a: [],
    original_tag_group_b: [],
    original_tag_group_c: [],
    original_tag_group_d: [],
    original_tag_group_e: []
  }
}

const readStoreQuery = computed<ReadStoreQuery>(() => {
  return {
    companyUuid: currentCompanyUuid.value,
    params: {
      page: 1,
      per: 9999,
      q: {
        m: 'or'
      },
      search: {
        brand_ids: formValues.value.brands,
        area_ids: formValues.value.areas,
        franchisee_ids: formValues.value.franchisees,
        user_company_ids: formValues.value.user_companies,
        prefectures: formValues.value.prefectures,
        original_tag_group_a_tag_ids: formValues.value.original_tag_group_a,
        original_tag_group_b_tag_ids: formValues.value.original_tag_group_b,
        original_tag_group_c_tag_ids: formValues.value.original_tag_group_c,
        original_tag_group_d_tag_ids: formValues.value.original_tag_group_d,
        original_tag_group_e_tag_ids: formValues.value.original_tag_group_e,
      }
    },
  }
})

const loading = computed<boolean>(() => instagramBusinessAccountsLoading.value || instagramHashtagLoading.value || searchStoresLoading.value || searchAttributesLoading.value)

const submitDisabled = computed<boolean>(() => {
  // ハッシュタグ未入力 && 店舗未選択 の場合は保存できないようにする
  const enteredHashtag = hashtags.value.filter(v => v != '') // hashtagsは初期値ref([''])なので、単純にlengthで判定しないようにする
  // ハッシュタグ未入力 && 店舗未選択 もしくは ハッシュタグに#を含めている 場合は保存できないようにする
  return !enteredHashtag.length && !storeUuids.value.length || includeHashStr.value
})

const includeHashStr = computed<boolean>(() => {
  return hashtags.value.some(v => v.match(/#|\s/))
})

// 「すでにほかで設定されている店舗」がないか判定する
const containsConnectedStores = computed<boolean>(() => {
  let connectedStores: Store[] = []
  for (let account of instagramBusinessAccounts.value) {
    // 現在編集中のinstagramアカウントは除く
    if (account.username === instagramHashtagSetting.value.username) {
      continue
    }
    connectedStores = connectedStores.concat(account.stores)
  }
  let connectedStoreUUids: string[] = []
  connectedStoreUUids = connectedStores.map((v: Store) => v.uuid)

  return !!storeUuids.value.filter(v => connectedStoreUUids.includes(v)).length
})

const addTextField = (): void => {
  hashtags.value.push('')
}
const deleteHashtag = (index: number): void => {
  hashtags.value.splice(index, 1)
}
const updateHashtags = (index: number, value: string): void => {
  hashtags.value[index] = value
}

const createFilter = (): void => {
  const attributes = [
    brands.value,
    areas.value,
    franchisees.value,
    userCompanies.value,
  ]

  if(originalTagGroupA.value) attributes.push(originalTagGroupA.value)
  if(originalTagGroupB.value) attributes.push(originalTagGroupB.value)
  if(originalTagGroupC.value) attributes.push(originalTagGroupC.value)
  if(originalTagGroupD.value) attributes.push(originalTagGroupD.value)
  if(originalTagGroupE.value) attributes.push(originalTagGroupE.value)
  attributes.push(prefectures.value)

  filterItems.value = attributes.map((attr) => {
    if(attr && attr.kind){
      return addAttributes(attr, attr.kind)
    }
  })
}
const handleChangeFilterModalCheckBox = ({kind, id}: {kind: string, id:number}): void => {
  const newFormValues: FormValue = Object.assign({}, formValues.value)

  if(newFormValues[kind].includes(id)) {
    newFormValues[kind] = newFormValues[kind].filter((_id: number) => {
      return _id !== id
    })
  } else {
    newFormValues[kind].push(id)
  }

  setFormValues(newFormValues)

  readStores(readStoreQuery.value)
}

const handleOpenStoreModal = (): void => {
  if (instagramBusinessAccountId.value && storeUuids.value.length !== 0) {
    showStoreModal.value = true
  } else {
    showFilterModal.value = true
  }
}

const handleCloseFilterModal = () => {
  setFormValuesOfSnsInstagramHashtag({ stores: [...storeUuids.value] })
  showFilterModal.value = false
}

const handleSubmitFilterModal = (): void => {
  showFilterModal.value = false
  showStoreModal.value = true
}

const handleResetFilterModal = (): void => {
  setFormValues(formInitialValues())
  readStores(readStoreQuery.value)
}

const handleBack = (): void => {
  showStoreModal.value = false
  showFilterModal.value = true
}

const confirmStoreUuids = (ids: string[]): void => {
  showStoreModal.value = false

  storeUuids.value = ids
}

const cancelPage = (): void => {
  if (confirm(t('labels.cancelInput'))) {
    router.push({ name: 'CompaniesSnsInstagramHashtagList' })
  }
}

const saveHashtagSetting = async (): Promise<void> => {
  let isSuccess
  // ハッシュタグ配列中の空文字は送らないようにする
  const payloadHashtags = hashtags.value.filter(v => v).map(v => v.trim())
  if (containsConnectedStores.value) {
    if (confirm(t('pages.companies.instagramHashtag.form.alert'))) {
      isSuccess = await updateHashtagSetting({
        payload: { settings: { store_uuids: storeUuids.value, keywords: payloadHashtags } },
        instagramBusinessAccountId: instagramBusinessAccountId.value,
      })
    }
  } else {
    isSuccess = await updateHashtagSetting({
      payload: { settings: { store_uuids: storeUuids.value, keywords: payloadHashtags } },
      instagramBusinessAccountId: instagramBusinessAccountId.value,
    })
  }

  if (isSuccess) router.push({ name: 'CompaniesSnsInstagramHashtagList' })
}

const load = async (): Promise<void> => {
  await Promise.all([
    readSearchTagGroups(),
    readHashtagSetting(instagramBusinessAccountId.value),
    readInstagramBusinessAccounts()
  ])
  if (instagramHashtagSetting.value) {
    storeUuids.value = instagramHashtagSetting.value.stores.map((v: Store) => v.uuid)
    hashtags.value = instagramHashtagSetting.value.hashTags
  }
  setFormValues(formInitialValues())
  createFilter()
  readStores(readStoreQuery.value)
}
load()
</script>
<template>
  <div class="p-box">
    <div class="p-box__header">
      <KkButton
        color="secondary"
        :to="{ name: 'CompaniesSnsInstagramHashtagList' }"
        :text="t('labels.backToList')"
      />
    </div>
    <KkLoading v-if="loading" />
    <div v-else class="p-box__body">
      <form class="c-grid__items" onsubmit="return false">
        <section class="main-box c-grid__item c-grid__item__s__12 c-grid__item__m__7 c-grid__item__l__7 settings">
          <KkBox border>
            <template #content>
              <h3>{{ t('pages.companies.instagramHashtag.form.hashtagSetting') }}</h3>
              <KkBox border class="accountInfo">
                <template #content>
                  <KkAccountInfoLabel
                    :image-url="instagramHashtagSetting.picture.url"
                    sns-type="instagram"
                    :name="instagramHashtagSetting.name"
                    :page-id="instagramHashtagSetting.username"
                  />
                </template>
              </KkBox>
              <KkBox border>
                <template #content>
                  <KkFormLabel :text="`${ t('pages.companies.instagramHashtag.form.hashtag') }`" />
                  <div v-for="index in hashtags.length" :key="index" class="form">
                    <KkTextField class="c-textField" :model-value="hashtags[index - 1]" :placeholder="t('messages.noSettings')" @update:model-value="updateHashtags(index - 1, $event)" />
                    <button type="button" class="deleteHashtag" @click="deleteHashtag(index - 1)">
                      <KkIcon name="delete" color="rgba(58, 59, 63, 0.7)" />
                    </button>
                  </div>
                  <KkAddLabel
                    type="button"
                    :label="t('pages.companies.instagramHashtag.form.addHashtag')"
                    @click="addTextField"
                  />
                  <KkValidationError :text="includeHashStr ? t('pages.companies.instagramHashtag.form.error.includeHashTag') : ''" />
                </template>
              </KkBox>
            </template>
          </KkBox>
        </section>
        <section class="sub-box c-grid__item c-grid__item__s__12 c-grid__item__m__5 c-grid__item__l__5 preview-box">
          <KkSelectStore
            :target-store-label="t('pages.companies.instagramHashtag.form.targetStore')"
            :title="t('pages.companies.instagramHashtag.form.storeSetting')"
            :selected-store-count="storeUuids.length"
            :disabled="searchAttributesLoading || searchStoresLoading"
            @open-store-modal="handleOpenStoreModal"
          >
            <div class="selectStore__buttons">
              <KkButton
                :text="t('labels.cancel')"
                color="secondary"
                @click="cancelPage"
              />
              <KkButton
                :text="t('labels.save')"
                :disabled="submitDisabled"
                @click="saveHashtagSetting"
              />
            </div>
          </KkSelectStore>
        </section>
      </form>
    </div>
    <KkFilterModal
      :is-visible="showFilterModal"
      :filter-items="filterItems"
      :form-values="formValues"
      :total-count="filteredCount"
      :submit-button-text="t('labels.checkTargetStores')"
      @close="handleCloseFilterModal"
      @submit="handleSubmitFilterModal"
      @reset="handleResetFilterModal"
      @change-check-box="handleChangeFilterModalCheckBox"
    />
    <KkStoreModal
      v-if="showStoreModal"
      store-name="companiesSnsInstagramHashtag"
      :only-gmb-connected="false"
      :search-stores="stores"
      :form-values="instagramHashtagFormValues"
      :store-total-count="storeTotalCount"
      @close-store-modal="confirmStoreUuids"
      @back-filter-modal="handleBack"
      @set-form-values="setInstagramHashtagFormValues"
      @toggle-filter-values="toggleInstagramHashtagFormValue"
      @set-filter-values="setInstagramHashtagFilterValues"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '../../../../../../stylesheets/biz_spa/foundation/var';
.p-box {
  &__header {
    .button {
      margin-left: auto;
    }
  }
}
.accountInfo {
  background-color: #F5F6F9;
  margin-top: 24px;
  margin-bottom: 32px;
}
.form {
  display: flex;

  &:not(:first-child) {
    margin-top: 16px;
  }
}
.deleteHashtag {
  margin-left: 16px;
}
.formLabel {
  display: block;
}
.selectStore {
  &__buttons {
    display: flex;
    align-items: center;
    margin: 32px -8px 0 -8px;

    button {
      flex-grow: 1;
      margin: 0 8px 8px;
    }
  }
}
</style>
